package com.downdogapp.client.widget

import com.downdogapp.*
import com.downdogapp.AlignItems.STRETCH
import com.downdogapp.Color.Companion.BLACK
import com.downdogapp.Color.Companion.WHITE
import com.downdogapp.Color.Companion.white
import com.downdogapp.Cursor.POINTER
import com.downdogapp.Display.INLINE_BLOCK
import com.downdogapp.FlexWrap.WRAP
import com.downdogapp.FontWeight.LIGHT
import com.downdogapp.FontWeight.SEMIBOLD
import com.downdogapp.Position.ABSOLUTE
import com.downdogapp.Position.RELATIVE
import com.downdogapp.Size.Companion.percent
import com.downdogapp.Size.Companion.px
import com.downdogapp.client.AbstractView
import com.downdogapp.client.MANIFEST
import com.downdogapp.client.api.MixGroup
import com.downdogapp.client.controllers.MixViewController
import com.downdogapp.client.resources.Images
import com.downdogapp.client.resources.image
import com.downdogapp.client.views.MixView
import com.downdogapp.client.widget.RangeSlider.Companion.rangeSlider
import kotlinx.dom.clear
import org.w3c.dom.HTMLDivElement
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLImageElement

class MixViewRow(private val group: MixGroup,
                 private val controller: MixViewController) : AbstractView() {

  private val expanded get() = controller.isExpanded(group)

  private var expandIcon: HTMLImageElement
  private var subgroupsContainer: HTMLDivElement
  private var sliderColorsContainer: HTMLDivElement
  private val slider: RangeSlider

  private val numSliderIncrements = MANIFEST.mixGroupNumIncrements + 1 // Extra increment for only column

  private fun groupAmountToSliderValue(value: Int) =
    (value.toDouble() / 100) * (numSliderIncrements - 1) / numSliderIncrements

  private val RangeSlider.groupAmount
    get() =
      ((value * numSliderIncrements / (numSliderIncrements - 1)).coerceAtMost(1.0) * 100).toInt()

  override val root = createDiv {
    bgColor = BLACK
    width = MixView.MIX_VIEW_WIDTH

    horizontalFlexBox(verticalAlign = STRETCH) {

      onClick {
        controller.toggleExpanded(group)
        blur()
      }

      horizontalFlexBox(verticalAlign = AlignItems.CENTER) {
        width = px(180)
        paddingLeft = px(20)
        paddingVertical = px(20)

        expandIcon = imageView(if (expanded) Images.MINUS_FOCUSED else Images.PLUS_UNFOCUSED) {
          marginRight = px(10)
          opacity = if (controller.hasSubgroups(group)) 1.0 else 0.0
        }

        label(text = group.label, fontSize = 14, fontWeight = SEMIBOLD)
      }

      div {
        flexGrow = 1.0
        display = Display.GRID // Prevent Safari behavior with height 100%
        onClick(cursorPointer = false)

        horizontalFlexBox(verticalAlign = AlignItems.CENTER) {
          marginRight = px(30)
          height = percent(100)
          position = RELATIVE

          sliderColorsContainer = horizontalFlexBox {
            position = RELATIVE
            width = percent(100)
            height = px(10)
          }
          horizontalFlexBox {
            position = ABSOLUTE
            width = percent(100)
            height = percent(100)
            top = px(0)
            left = px(0)

            slider = rangeSlider(
              valueRange = 0.0..1.0,
              startValue = 0.0,
              innerRangeMin = 0,
              innerRangeMax = numSliderIncrements,
              className = "focus-area-slider",
              onValueChanged = {
                controller.setGroupAmount(group, groupAmount, value == 1.0)
              }
            ) {
              cursor = POINTER
            }
          }
        }
      }
    }

    subgroupsContainer = horizontalFlexBox(flexWrap = WRAP) {
      paddingHorizontal = px(40)
      if (controller.hasSubgroups(group)) {
        paddingTop = px(8)
        paddingBottom = px(20)
      }
    }
  }

  fun refreshView() {
    val sliderTargetValue =
      if (controller.isOnlyEnabled(group))
        1.0
      else
        groupAmountToSliderValue(controller.getGroupAmount(group))

    slider.setValueNoUpdate(sliderTargetValue)

    populateSubgroups()
    updateSliderColors()

    if (expanded) {
      subgroupsContainer.show()
    } else {
      subgroupsContainer.hide()
    }
    expandIcon.image = if (expanded) Images.MINUS_FOCUSED else Images.PLUS_UNFOCUSED
    root.opacity = if (controller.isDisabled(group)) 0.3 else 1.0
  }

  private fun updateSliderColors() {
    sliderColorsContainer.apply {
      clear()
      if (controller.isOnlyEnabled(group)) {
        div {
          bgColor = Color.SLIDER_MEDIUM_BLUE
          borderRadius = px(50)
          width = percent(100)
        }
      } else {
        div { // Last section
          position = ABSOLUTE
          bgColor = Color.SLIDER_DARK_BLUE
          right = px(5)
          width = percent(100 / numSliderIncrements)
          height = px(10)
        }
        div { // Last section dot
          position = ABSOLUTE
          width = px(15)
          height = px(15)
          right = px(0)
          marginTop = px(-2.5)
          bgColor = Color.SLIDER_DARK_BLUE
          borderRadius = percent(50)
          display = INLINE_BLOCK
        }
        div { // First Section
          bgColor = Color.SLIDER_MEDIUM_BLUE
          borderTopLeftRadius = px(50)
          borderBottomLeftRadius = px(50)
          width = percent(100 * groupAmountToSliderValue(controller.getGroupAmount(group)))
        }
        div { // Middle Section
          bgColor = Color.SLIDER_GREY
          position = ABSOLUTE
          borderTopRightRadius = px(50)
          borderBottomRightRadius = px(50)
          right = percent(100 / numSliderIncrements)
          width = percent(100 * groupAmountToSliderValue(100 - controller.getGroupAmount(group)))
          height = px(10)
        }
      }
    }
  }

  private fun populateSubgroups() {
    subgroupsContainer.clear()
    controller.getSubgroupsForGroup(group).map { subgroup ->
      val isEnabled = controller.isSubgroupEnabled(subgroup)
      subgroupsContainer.div {
        borderRadius = px(50)
        padding = px(10)
        margin = px(5)

        bgColor = if (isEnabled) WHITE else BLACK
        border = Border.solid(px(1), if (isEnabled) WHITE else white(0.4))

        label(text = subgroup.label, fontSize = 14, fontWeight = LIGHT, color = if (isEnabled) BLACK else WHITE) {
          opacity = if (isEnabled) 1.0 else 0.5
        }

        onClick {
          if (!controller.isDisabled(group)) {
            controller.toggleSubgroupEnabled(subgroup)
            populateSubgroups()
          } else {
            blur()
          }
        }
      }
    }
  }
}

fun HTMLElement.mixViewRow(mixGroup: MixGroup,
                           controller: MixViewController): MixViewRow {
  val view = MixViewRow(mixGroup, controller)
  append(view.root)
  return view
}
